import { useI18n } from "@core/i18n/i18n";
import { useHistory } from "@core/routing/use-history";
import { useQueryParams } from "@core/routing/use-query-params";
import { analyticsService, cookiebotManager } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import styled from "@emotion/styled";
import { Page } from "@ui/components/common/page";
import { PageHeader } from "@ui/components/common/page-header";
import { RoutesPath } from "@ui/navigation/routes";
import { AccountTab } from "@ui/pages/settings/tabs/account-tab";
import { NotificationPreferencesTab } from "@ui/pages/settings/tabs/notification-preferences-tab";
import { physioMeTabletBreakpoint } from "@ui/style";
import { colors } from "@ui/style/colors";
import { TabPanel, TabView } from "primereact/tabview";
import React, { useCallback, useState } from "react";
import { ValidateAccountCard } from "@ui/pages/home/components/validate-account-card";

export enum TabSettings {
	Account = "account",
	NotificationPreferences = "notificationPreferences",
	Cookies = "cookies",
}

const tabIndices = [TabSettings.Account, TabSettings.NotificationPreferences, TabSettings.Cookies];

export const SettingsPage = (): JSX.Element => {
	const { format } = useI18n();
	const { active } = useQueryParams<RoutesPath.Settings>();
	const { updateHistory } = useHistory<RoutesPath.Settings>();
	const [activeTabIndex, setActiveTabIndex] = useState(tabIndices.indexOf(active ?? TabSettings.Account));

	const selectTab = useCallback(
		(tabIndex: number) => {
			setActiveTabIndex(tabIndex);
			updateHistory({ active: tabIndices[tabIndex] });
		},
		[updateHistory],
	);

	const openCookieDialog = useCallback(() => {
		cookiebotManager.openConsentDialog();
		analyticsService.trackEvent({
			category: AnalyticsCategory.PERMISSIONS,
			action: "openCookieSettings",
		});
	}, []);

	return (
		<StyledPage>
			<ValidateAccountCard hideForValidatedPt={true} showForPendingPt={true} />
			<PageHeader
				title={format("tabbar.settings")}
				tertiaryButton={{ label: format("settings.manageCookiesButton"), onClick: openCookieDialog }}
			/>
			<Card className="p-card">
				<StyledTabView activeIndex={activeTabIndex} onTabChange={e => selectTab(e.index)}>
					<TabPanel header={format("settings.accountTab.header")}>
						<AccountTab />
					</TabPanel>
					<TabPanel header={format("settings.notificationPreferences")}>
						<NotificationPreferencesTab />
					</TabPanel>
				</StyledTabView>
			</Card>
		</StyledPage>
	);
};

const StyledPage = styled(Page)`
	@media ${physioMeTabletBreakpoint} {
		padding-left: 20px;
		padding-right: 20px;
		max-width: 100%;
	}
`;

const StyledTabView = styled(TabView)`
	@media ${physioMeTabletBreakpoint} {
		.p-tabview-nav {
			background-color: ${colors.primaryBackground} !important;
		}

		.p-tabview-nav-link {
			background-color: ${colors.primaryBackground} !important;
			justify-content: center !important;
		}

		.p-tabview-panels {
			padding: 0 !important;
		}
	}
`;

const Card = styled.div`
	padding: 24px 40px;
	width: 1200px;
	max-width: 100%;
	@media ${physioMeTabletBreakpoint} {
		padding: 0;
		flex: 1;
		border-radius: 0;
	}
`;
